import type { Head, LinkTag, MetaAttribute, AttributeMetaTag } from './types';
import { MetaAttributeKey } from './types';
import { MetaElements } from './types';
import { useSSRContext } from 'vue';

declare global {
  interface Window {
    seamlessRouter: {
      mainNamespace: string;
    };
  }
}

/**
 *
 * handles the meta-tags manipulation on the server side.
 * updates the SSRContext with the meta-tags that is later used by the SSR plugin
 *
 * @private
 * @ignore
 * @param {Head} head The head object that contains the meta-tags
 * @return {*} {void}
 **/
function serverMetaTagsHandler(head: Head): void {
  const context = useSSRContext();
  if (context) {
    context.head = head;
  }
}

/**
 *
 * handles all the logic behind the requests.
 * checks if the meta-tags already exist and if so, updates them.
 * if the tags do not exist, it creates them and appends them to the head
 * can also handle the title tag and the link tag
 *
 * @private
 * @ignore
 * @param {Head} head The head object that contains the meta-tags
 * @return {*} {void}
 **/
function clientMetaTagsHandler(providedHead: Head): void {
  const head = document.querySelector(MetaElements.HEAD);
  if (!head) {
    return;
  }

  if (providedHead.title) {
    const currentMetaTag = document.querySelector(MetaElements.TITLE);
    if (currentMetaTag && currentMetaTag.innerText !== providedHead.title) {
      currentMetaTag.innerText = providedHead.title;
    } else {
      const newMetaTag = document.createElement(MetaElements.TITLE);
      newMetaTag.innerText = providedHead.title;
      head.appendChild(newMetaTag);
    }
  }

  if (providedHead.meta) {
    providedHead.meta.forEach((metaTag: AttributeMetaTag) => {
      const metAttribute = metaTag.attribute;
      const currentMetaTag = document.querySelector(`meta[${metAttribute.key}='${metaTag.attribute.metaValue}']`);
      if (currentMetaTag && currentMetaTag.getAttribute('content') !== metaTag.content) {
        currentMetaTag.setAttribute('content', metaTag.content);
      } else {
        const newMetaTag = document.createElement(MetaElements.META);
        newMetaTag.setAttribute(metAttribute.key, metaTag.attribute.metaValue);
        newMetaTag.setAttribute('content', metaTag.content);
        head.appendChild(newMetaTag);
      }
    });
  }

  /**
   * For non-canonical link tags, this never updates an existing link tag, it only creates new ones
   * eg: <link rel="dns-prefetch" href="//assets.example.com" />
   * since the attributes are not sufficient to identify a unique link tag
   * so the logic checks for the pair of attribute and href
   *
   * For canonical link tags, there should only be one per page,
   * so the attributes alone (rel="canonical") are sufficient to identify it.
   * If a canonical already exists, its href is updated instead of creating a duplicate.
   */
  if (providedHead.link) {
    providedHead.link.forEach((linkTag: LinkTag) => {
      const isCanonical = linkTag.attribute.some((attr) => attr.key === 'rel' && attr.metaValue === 'canonical');

      // Construct a selector based on attributes only
      const attrSelector = linkTag.attribute.reduce((acc, attr) => {
        return `${acc}[${attr.key}="${attr.metaValue}"]`;
      }, 'link');

      // For canonical links, match by attributes only; for others, include href
      const selector = isCanonical ? attrSelector : `${attrSelector}[href="${linkTag.href}"]`;
      const existingLinkTag = document.querySelector(selector);

      if (existingLinkTag) {
        // Update href only for canonical links when it has changed
        if (isCanonical && existingLinkTag.getAttribute('href') !== linkTag.href) {
          existingLinkTag.setAttribute('href', linkTag.href);
        }
      } else {
        const newLinkTag = document.createElement(MetaElements.LINK);
        linkTag.attribute.forEach((attribute) => {
          newLinkTag.setAttribute(attribute.key, attribute.metaValue);
        });
        newLinkTag.setAttribute('href', linkTag.href);
        head.appendChild(newLinkTag);
      }
    });
  }
}

/**
 *
 * checks if a component has permissions to change the meta-tags based on the page's mainRoutingNamespace
 * If the routerNamespace name from the component is the same as the mainRoutingNamespace the method
 * will return true
 *
 * @param {string} routerNamespace The component identifier
 * @return {*} {boolean}
 **/

function hasRequestPermission(routerNamespace: string): boolean {
  const topMostWindow = window.top || window;
  const mainNamespace = topMostWindow.seamlessRouter ? topMostWindow.seamlessRouter.mainNamespace : null;
  if (!mainNamespace) {
    return false;
  }
  return routerNamespace === mainNamespace;
}

/**
 *
 * updates the meta-tags based on the provided head object.
 * if the component has permission to update the meta-tags it will do so.
 * if the component does not have permission to update the meta-tags it will do nothing.
 * if the component is not running in the browser it will update the SSRContext with the meta-tags
 *
 * @param {Head} head The head object that contains the meta-tags
 * @param {string} routerNamespace The component identifier
 * @return {*} {void}
 **/
function updateMetaTags(head: Head, routerNamespace: string): void {
  const isBrowser = typeof window !== 'undefined';
  if (!isBrowser) {
    serverMetaTagsHandler(head);
    return;
  }
  const requestPermission = hasRequestPermission(routerNamespace);
  if (requestPermission) {
    clientMetaTagsHandler(head);
  }
}

export { updateMetaTags, AttributeMetaTag, MetaAttributeKey, MetaAttribute, MetaElements, Head, LinkTag };
export { useJsonLd, serializeJsonLd, JSON_LD_ID_ATTRIBUTE } from './jsonLd';
export type { JsonLdSchema, StructuredDataEntry, UseJsonLdOptions } from './types';
